import { hasRTSPPublication } from "../utils/streamOutputs";
import RTSPLinks from "./RTSPLinks";
import SeiStatus from "./SeiStatus";
import WhipStatus from "./WhipStatus";
import LlHlsLinks from "./LlHlsLinks";
import { EMPTY_BITRATE_CHART, type BitrateChartData } from "../utils/bitrateHistory";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, type GridColDef, type GridRowSelectionModel } from "@mui/x-data-grid";
import ConnectedTvIcon from "@mui/icons-material/ConnectedTv";
import PreviewIcon from "@mui/icons-material/Preview";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import type { StreamSummary } from "../types/streamSummary";
import StreamState, { type StreamStateValue } from "./StreamState";
import SreamStateSpinner from "./SreamStateSpinner";
import StreamBitrateChartTiny from "./StreamBitrateChartTiny";
import StreamPreviewPopover, { type StreamPreviewPopoverOpenAnimation } from "./StreamPreviewPopover";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";
import { useAppContext } from "../context/AppContext";
import {
  isStreamRunningState,
  isStreamConfigReadOnly,
  isStreamStartingState,
  shouldShowStreamActivitySpinner
} from "../utils/streamState";
import {
  extractRecastOutputs,
  resolveOutputState,
  type StreamOutputInfo,
  type StreamOutputStatsSnapshot,
  isRecastMode,
  isRecastOnlyMode
} from "../utils/streamOutputs";

type StreamTableRow = {
  id: string;
  sensor: string;
  platformName?: string;
  type: string;
  url: string;
  active: boolean;
  stream: StreamSummary;
  streamState: StreamStateValue | undefined;
  streamHasError: boolean;
  isStreamStarting: boolean;
  isStreamRunning: boolean;
  errorCaption: string | null;
  streamStateTooltip: string | undefined;
  isDataPayload: boolean;
  isRecastOnly: boolean;
  fileSourceInfo?: FileSourceInfoSnapshot;
  outputs: StreamOutputInfo[];
  outputStats?: Map<string, StreamOutputStatsSnapshot>;
};

type StreamTableProps = {
  streams: StreamSummary[];
  canManageStreams: boolean;
  canViewDetectionInfo: boolean;
  commandsInFlight: Record<string, "start" | "stop">;
  streamBitratesById: Map<string, BitrateChartData>;
  fileSourceInfoByStream: Map<string, FileSourceInfoSnapshot>;
  onCommand: (args: { streamId: string; command: "start" | "stop" }) => void;
  onOpenEdit: (stream: StreamSummary) => void;
  onOpenDelete: (stream: StreamSummary) => void;
  onOpenDetection: (stream: StreamSummary) => void;
  onToggleTimeline: (stream: StreamSummary) => void;
  onToggleCalendar: (stream: StreamSummary) => void;
  onOpenPlayer: (stream: StreamSummary) => void;
  timelineStreamId: string | null;
  calendarStreamId: string | null;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
  getOutputState: (streamId: string, outputId: string) => StreamStateValue | undefined;
  getOutputStateMessage: (streamId: string, outputId: string) => string | null;
  outputStatsByStream: Map<string, Map<string, StreamOutputStatsSnapshot>>;
  selectedStreamIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  previewOpenAnimation?: StreamPreviewPopoverOpenAnimation;
};

const StreamTable = ({
  streams,
  canManageStreams,
  canViewDetectionInfo,
  commandsInFlight,
  streamBitratesById,
  fileSourceInfoByStream,
  onCommand,
  onOpenEdit,
  onOpenDelete,
  onOpenDetection,
  onToggleTimeline,
  onToggleCalendar,
  onOpenPlayer,
  timelineStreamId,
  calendarStreamId,
  getStreamState,
  getStreamStateMessage,
  getOutputState,
  getOutputStateMessage,
  outputStatsByStream,
  selectedStreamIds,
  onSelectionChange,
  previewOpenAnimation = "none"
}: StreamTableProps) => {
  const { serverConfig } = useAppContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(Infinity);
  const hasRows = streams.length > 0;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => setContainerWidth(container.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasRows]);
  const selectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: selectedStreamIds?.length ? new Set(selectedStreamIds) : new Set()
    }),
    [selectedStreamIds]
  );

  const hasRecastStreams = useMemo(
    () =>
      streams.some((stream) => isRecastMode(stream.mode)),
    [streams]
  );

  const rows = useMemo<StreamTableRow[]>(
    () =>
      streams.map((stream) => {
        const streamState = getStreamState(stream.id);
        const streamStateMessage = getStreamStateMessage(stream.id);
        const streamHasError = streamState === "error";
        const isStreamStarting = isStreamStartingState(streamState, streamStateMessage);
        const isDataPayload = (stream.payloadType ?? "").trim().toLowerCase() === "data";
        const isRecastOnly = isRecastOnlyMode(stream.mode);
        const hasRecastMode = isRecastMode(stream.mode);
        const trimmedErrorMessage = streamHasError && typeof streamStateMessage === "string"
          ? streamStateMessage.trim()
          : "";
        const errorCaption = streamHasError && trimmedErrorMessage ? `Error: ${trimmedErrorMessage}` : null;
        const streamStateTooltip = errorCaption ?? undefined;
        const isStreamRunning = isStreamRunningState(streamState, streamStateMessage) || hasRTSPPublication(outputStatsByStream.get(stream.id));

        return {
          id: stream.id,
          sensor: stream.sensor,
          platformName: stream.platformName,
          type: stream.type,
          url: stream.url,
          active: stream.active,
          stream,
          streamState,
          streamHasError,
          isStreamStarting,
          isStreamRunning,
          errorCaption,
          streamStateTooltip,
          isDataPayload,
          isRecastOnly,
          fileSourceInfo: fileSourceInfoByStream.get(stream.id),
          outputs: hasRecastStreams && hasRecastMode ? extractRecastOutputs(stream.targets) : [],
          outputStats: outputStatsByStream.get(stream.id)
        };
      }),
    [streams, getStreamState, getStreamStateMessage, hasRecastStreams, outputStatsByStream, fileSourceInfoByStream]
  );

  const columns = useMemo<GridColDef<StreamTableRow>[]>(
    () => {
      let compactOutputs = false;
      const baseColumns: GridColDef<StreamTableRow>[] = [
        {
          field: "state",
          headerName: "Status",
          minWidth: 60,
          flex: 0.5,
          sortable: false,
          valueGetter: (params: { row: StreamTableRow }) => params?.row?.streamState ?? null,
          renderCell: (params) => {
            const { streamState, errorCaption, streamStateTooltip, fileSourceInfo } = params.row;
            const streamHasError = streamState === "error";
            const showSpinner = shouldShowStreamActivitySpinner(
              streamState,
              getStreamStateMessage(params.row.id)
            );

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
                <StreamState state={streamState} tooltip={streamStateTooltip} />
                <SreamStateSpinner
                  streamHasError={streamHasError}
                  showSpinner={showSpinner}
                  errorCaption={errorCaption}
                  progress={fileSourceInfo?.progress}
                  position={fileSourceInfo?.position}
                  loopCount={fileSourceInfo?.loopCount}
                />
              </Box>
            );
          }
        },
        {
          field: "sensor",
          headerName: "Channel",
          flex: 1.5,
          minWidth: 80,
          renderCell: (params) => {
            const description = params.row.stream.description?.trim();
            return (
              <Tooltip
                title={
                  <>
                    <div>{params.row.url}</div>
                    {description && <div>{description}</div>}
                  </>
                }
                placement="top"
                describeChild
              >
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {params.row.sensor}
                  </Typography>
                </Box>
              </Tooltip>
            );
          }
        },
        {
          field: "platformName",
          headerName: "Platform",
          flex: 1,
          minWidth: 60,
          sortable: true,
          // Case-insensitive comparator that handles empty/missing values
          sortComparator: (v1: string | undefined, v2: string | undefined) => {
            const val1 = v1 ?? "";
            const val2 = v2 ?? "";
            return val1.localeCompare(val2, undefined, { sensitivity: "base" });
          },
          renderCell: (params) => (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="body2">
                {params.row.platformName ?? params.row.stream.platformName ?? "—"}
              </Typography>
            </Box>
          )
        },
        {
          field: "bitrate",
          headerName: "Bitrate",
          minWidth: 120,
          flex: 1.2,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const bitrates = streamBitratesById.get(params.row.id) ?? EMPTY_BITRATE_CHART;
            return (
              <Box sx={{ width: "100%", height: "100%" }}>
                <StreamBitrateChartTiny bitrates={bitrates} />
              </Box>
            );
          }
        },
      ];

      if (hasRecastStreams) {
        baseColumns.push({
          field: "outputs",
          headerName: "Outputs",
          minWidth: 160,
          flex: 1.2,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const outputs = params.row.outputs ?? [];
            if (!outputs.length) {
              return (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              );
            }
            const outputStats = params.row.outputStats;
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: compactOutputs ? "nowrap" : "wrap", width: "100%" }}>
                {outputs.map((output) => {
                  const stats = outputStats?.get(output.id);
                  const hasBytesSent = stats?.whip?.bytesAvailable !== false && typeof stats?.bytesSent === "number";
                  const rawOutputState = getOutputState(params.row.id, output.id);
                  const outputState = resolveOutputState(rawOutputState, stats, params.row.isStreamRunning, output.enabled);
                  const hasBitrate = outputState === "online" &&
                    typeof stats?.bitrate === "number" &&
                    Number.isFinite(stats.bitrate) &&
                    stats.bitrate > 0;
                  const outputError = outputState === "error"
                    ? getOutputStateMessage(params.row.id, output.id)
                    : null;
                  const bitrateLabel = hasBitrate ? `${stats!.bitrate.toFixed(2)}` : "";
                  const tooltipContent = (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, maxWidth: 320 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {output.name}
                      </Typography>
                      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                        {stats?.playbackUrl || output.url}
                      </Typography>
                      <Typography variant="caption">
                        Bitrate: {hasBitrate ? `${stats!.bitrate.toFixed(2)} Mbit/s` : "—"}
                      </Typography>
                      <Typography variant="caption">
                        Sent: {hasBytesSent ? `${(stats!.bytesSent / (1024 * 1024)).toFixed(2)} MB` : "—"}
                      </Typography>
                      <SeiStatus stats={stats} running={params.row.isStreamRunning && output.enabled !== false} />
                      {outputError ? (
                        <Typography variant="caption" color="error.light" sx={{ wordBreak: "break-word" }}>
                          Error: {outputError}
                        </Typography>
                      ) : null}
                    </Box>
                  );

                  return (
                    <Box key={output.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      <StreamState
                        state={outputState}
                        size={12}
                        tooltip={tooltipContent}
                      />
                      {output.type === "whip" && <WhipStatus stats={stats} />}
                      {output.type === "rtsp" && <RTSPLinks stats={stats} fallback={output.url} />}
                      {output.type === "llhls" && <LlHlsLinks stats={stats} fallback={output.url} />}
                      {bitrateLabel && !compactOutputs ? (
                        <Tooltip title={tooltipContent}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", lineHeight: 1 }}
                          >
                            {bitrateLabel}
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            );
          }
        });
      }

      const allColumns: GridColDef<StreamTableRow>[] = [
        ...baseColumns,
        {
          field: "active",
          headerName: "Active",
          flex: 0.6,
          minWidth: 80,
          sortable: true,
          renderCell: (params) => (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Chip
                label={params.row.active ? "Active" : "Inactive"}
                color={params.row.active ? "success" : "default"}
                size="small"
                variant="outlined"
                // variant={params.row.active ? "filled" : "outlined"}
              />
            </Box>
          )
        },
        {
          field: "type",
          headerName: "Type",
          flex: 0.5,
          minWidth: 50,
          valueGetter: (params: { row: StreamTableRow }) => params?.row?.type?.toUpperCase?.() ?? params?.row?.stream?.type?.toUpperCase?.() ?? "",
          renderCell: (params) => (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                {(params.row.type || params.row.stream.type).toUpperCase()}
              </Typography>
            </Box>
          )
        },
        {
          field: "actions",
          headerName: "Actions",
          sortable: false,
          filterable: false,
          // Reserve room for playback, detection, timeline, calendar, config, and control.
          minWidth: 240,
          renderCell: (params) => {
            const { stream, streamState, isStreamRunning, isStreamStarting, streamHasError, isDataPayload, isRecastOnly } = params.row;
            const isTimelineSelected = timelineStreamId === stream.id;
            const isCalendarSelected = calendarStreamId === stream.id;
            const pendingCommand = commandsInFlight[stream.id];
            const configReadOnly = isStreamConfigReadOnly(streamState, getStreamStateMessage(stream.id), pendingCommand);
            const isCommandPending = pendingCommand === "start" ? !isStreamRunning : pendingCommand === "stop" && isStreamRunning;
            const controlDisabled = isCommandPending || isStreamStarting || (!stream.active && !isStreamRunning);
            const controlTooltip = isCommandPending
              ? "Sending command..."
              : isStreamStarting
                ? "Starting"
              : isStreamRunning
                ? streamHasError
                  ? "Stop stream to clear error"
                  : "Stop stream"
                : !stream.active
                  ? "Stream must be active to control"
                  : "Start stream";

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", flexShrink: 0, height: "100%" }}>
                {isStreamRunning ? (
                  <>
                    {!isDataPayload && !isRecastOnly ? (
                      <StreamPreviewPopover
                        streamId={stream.id}
                        title={stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor}
                        streamState={streamState}
                        disabled={!serverConfig.enablePreview}
                        openAnimation={previewOpenAnimation}
                      >
                        <IconButton size="small" onClick={() => onOpenPlayer(stream)}>
                          <ConnectedTvIcon fontSize="small" />
                        </IconButton>
                      </StreamPreviewPopover>
                    ) : null}
                    {!isDataPayload && isRecastOnly && serverConfig.enablePreview ? (
                      <StreamPreviewPopover
                        streamId={stream.id}
                        title={stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor}
                        streamState={streamState}
                        openAnimation={previewOpenAnimation}
                      >
                        <IconButton size="small">
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </StreamPreviewPopover>
                    ) : null}
                    {!isDataPayload && canViewDetectionInfo ? (
                      <Tooltip title="Stream detection info">
                        <IconButton size="small" onClick={() => onOpenDetection(stream)}>
                          <TroubleshootIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Stream events timeline">
                      <IconButton
                        size="small"
                        color={isTimelineSelected ? "primary" : "default"}
                        aria-pressed={isTimelineSelected}
                        onClick={() => onToggleTimeline(stream)}
                      >
                        <ViewTimelineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}
                <Tooltip title="Stream calendar">
                  <IconButton
                    size="small"
                    color={isCalendarSelected ? "primary" : "default"}
                    aria-pressed={isCalendarSelected}
                    onClick={() => onToggleCalendar(stream)}
                  >
                    <CalendarMonthIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canManageStreams ? (
                  <>
                    <Tooltip title={configReadOnly ? "Show Config" : "Edit stream"}>
                      <IconButton size="small" aria-label={configReadOnly ? "Show Config" : "Edit stream"} onClick={() => onOpenEdit(stream)}>
                        {configReadOnly ? <SettingsIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={controlTooltip}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={isStreamRunning ? "Stop stream" : "Start stream"}
                          disabled={controlDisabled}
                          onClick={() =>
                            onCommand({
                              streamId: stream.id,
                              command: isStreamRunning ? "stop" : "start"
                            })
                          }
                        >
                          {isCommandPending || isStreamStarting ? (
                            <CircularProgress size={16} thickness={5} />
                          ) : isStreamRunning ? (
                            <StopCircleIcon fontSize="small" />
                          ) : (
                            <PlayCircleIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : null}
              </Box>
            );
          }
        }
      ];

      // Reserve the checkbox column and grid borders, then remove optional
      // columns in priority order before allowing the remaining columns to scroll.
      let requiredWidth = 52 + allColumns.reduce((sum, column) => sum + (column.minWidth ?? 50), 0);
      const hiddenFields = new Set<string>();
      for (const field of ["active", "type", "platformName"]) {
        if (containerWidth >= requiredWidth) break;
        hiddenFields.add(field);
        requiredWidth -= allColumns.find((column) => column.field === field)!.minWidth!;
      }

      compactOutputs = hiddenFields.has("platformName");
      if (compactOutputs) {
        const outputsColumn = allColumns.find((column) => column.field === "outputs");
        if (outputsColumn) {
          const maxOutputs = rows.reduce((max, row) => Math.max(max, row.outputs.length), 0);
          // 12px indicators, 6px gaps, and 20px cell padding. Keep the title legible.
          const width = Math.max(80, maxOutputs * 12 + Math.max(0, maxOutputs - 1) * 6 + 20);
          outputsColumn.flex = undefined;
          outputsColumn.width = width;
          outputsColumn.minWidth = width;
          outputsColumn.maxWidth = width;
          outputsColumn.disableColumnMenu = true;
          outputsColumn.resizable = false;
        }
      }

      return allColumns.filter((column) => !hiddenFields.has(column.field));
    },
    [
      canManageStreams,
      canViewDetectionInfo,
      commandsInFlight,
      onCommand,
      onOpenEdit,
      onOpenDetection,
      onToggleCalendar,
      onOpenPlayer,
      onToggleTimeline,
      timelineStreamId,
      calendarStreamId,
      containerWidth,
      rows,
      streamBitratesById,
      getOutputState,
      getOutputStateMessage,
      hasRecastStreams,
    ]
  );

  if (!rows.length) {
    return null;
  }

  return (
    <Box ref={containerRef} sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
      <DataGrid
        autoHeight
        density="compact"
        hideFooterSelectedRowCount
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        rowSelectionModel={selectionModel}
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={(params) => {
          onSelectionChange([String(params.id)]);
        }}
        onRowSelectionModelChange={(model: GridRowSelectionModel) => {
          const allRowIds = rows.map((row) => row.id);
          let ids: string[] = [];
          if (model && typeof model === "object" && "type" in model && "ids" in model) {
            const idsSet = model.ids instanceof Set ? model.ids : new Set(model.ids as Iterable<unknown>);
            if (model.type === "exclude") {
              ids = allRowIds.filter((id) => !idsSet.has(id)).map(String);
            } else {
              ids = Array.from(idsSet).map(String);
            }
          } else if (model && (model as any) instanceof Set) {
            ids = Array.from(model as Set<unknown>).map(String);
          } else if (Array.isArray(model)) {
            ids = (model as unknown[]).map(String);
          }
          onSelectionChange(ids);
        }}
        sx={{
          minWidth: 0,
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            py: 0.5
          }
        }}
        // Explicitly set the generic type parameter for rows
        getRowId={(row: StreamTableRow) => row.id}
      />
    </Box>
  );
};

export default StreamTable;
